// Audit/history view of cancelled transactions (original details, reason, user, linked journals).
import { useCallback, useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Ban } from 'lucide-react';
import { JournalSyncRecoveryAlert } from '../../components/accounting/JournalSyncRecoveryAlert';
import { CancelledTransactionDialog } from '../../components/accounting/CancelledTransactionDialog';
import { PosVoidHistory } from '../../components/accounting/PosVoidHistory';
import { cur } from '@/lib/currency';
import api from '../../lib/api';

const TYPES = { expenses: 'Expense', salary_payments: 'Salary payment', sales: 'Sale' };
const money = (n) => `${cur()} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Row({ r, onOpen }) {
  const o = r.original || {};
  return (
    <tr className="border-b border-border/60 align-top cursor-pointer hover:bg-muted/40" onClick={() => onOpen(r.id)} data-testid={`cancelled-row-${r.source_id}`}>
      <td className="py-2 pr-3 whitespace-nowrap text-xs">{String(r.cancelled_at || '').slice(0, 16).replace('T', ' ')}</td>
      <td className="py-2 pr-3"><Badge variant="outline">{TYPES[r.collection] || r.collection}</Badge></td>
      <td className="py-2 pr-3 text-xs">
        <div className="font-medium">{o.description || o.category || o.employee_name || o.invoice_number || String(r.source_id).slice(0, 8)}</div>
        <div className="text-muted-foreground">{String(o.date || '').slice(0, 10)} · {o.payment_mode || '—'}</div>
      </td>
      <td className="py-2 pr-3 text-right tabular-nums" data-testid={`cancelled-amount-${r.source_id}`}>{money(o.final_amount ?? o.amount)}</td>
      <td className="py-2 pr-3 text-xs" data-testid={`cancelled-reason-${r.source_id}`}>{r.reason}</td>
      <td className="py-2 pr-3 text-xs">{r.cancelled_by_name}</td>
      <td className="py-2 text-[11px] text-muted-foreground">
        {(r.original_journal_ids || []).length} original · {(r.reversal_journal_ids || []).length} reversal
      </td>
    </tr>
  );
}

export default function CancelledTransactionsPage() {
  const [rows, setRows] = useState([]);
  const [type, setType] = useState('all');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState(null);
  const load = useCallback(() => {
    api.get('/accounting/cancelled-transactions', { params: type === 'all' ? {} : { collection: type } })
      .then(({ data }) => setRows(data.rows || [])).catch(() => setRows([]));
  }, [type]);
  useEffect(() => { load(); }, [load]);
  const shown = rows.filter((r) => !q || JSON.stringify([r.reason, r.cancelled_by_name, r.original?.description, r.original?.category]).toLowerCase().includes(q.toLowerCase()));
  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="cancelled-transactions-page">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Ban size={22} className="text-rose-600" /> Cancelled Transactions</h1>
          <p className="text-sm text-muted-foreground">Posted transactions cancelled with a reversing journal. Originals are kept for audit.</p>
        </div>
        <JournalSyncRecoveryAlert onRecovered={load} />
        <Card>
          <CardHeader className="flex flex-row items-center gap-3 space-y-0">
            <CardTitle className="text-base flex-1">History ({shown.length})</CardTitle>
            <Input placeholder="Search reason / user / description" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs h-9" data-testid="cancelled-search" />
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="w-44 h-9" data-testid="cancelled-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {Object.entries(TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {shown.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center" data-testid="cancelled-empty">No cancelled transactions.</p>
            ) : (
              <table className="w-full text-sm" data-testid="cancelled-table">
                <thead><tr className="text-left text-xs text-muted-foreground border-b">
                  <th className="py-2 pr-3">Cancelled</th><th className="py-2 pr-3">Type</th><th className="py-2 pr-3">Original</th>
                  <th className="py-2 pr-3 text-right">Amount</th><th className="py-2 pr-3">Reason</th><th className="py-2 pr-3">By</th><th className="py-2">Journals</th>
                </tr></thead>
                <tbody>{shown.map((r) => <Row key={r.id} r={r} onOpen={setOpenId} />)}</tbody>
              </table>
            )}
          </CardContent>
        </Card>
        <PosVoidHistory />
      </div>
      <CancelledTransactionDialog archiveId={openId} onOpenChange={(o) => !o && setOpenId(null)} />
    </DashboardLayout>
  );
}
